import { MenuItem, notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { IsFavoriteIcon } from '@affine/core/components/pure/icons';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/favorite';
import type { FolderNode } from '@affine/core/modules/organize';
import {
  FolderTemplateService,
  TemplateDocService,
} from '@affine/core/modules/template-doc';
import { useI18n } from '@affine/i18n';
import { LocalWorkspaceIcon, TemplateIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

export const FavoriteFolderOperation = ({ id }: { id: string }) => {
  const t = useI18n();
  const compatibleFavoriteItemsAdapter = useService(
    CompatibleFavoriteItemsAdapter
  );

  const favorite = useLiveData(
    useMemo(() => {
      return compatibleFavoriteItemsAdapter.isFavorite$(id, 'folder');
    }, [compatibleFavoriteItemsAdapter, id])
  );

  return (
    <MenuItem
      prefixIcon={<IsFavoriteIcon favorite={favorite} />}
      onClick={() => compatibleFavoriteItemsAdapter.toggle(id, 'folder')}
    >
      {favorite
        ? t['com.affine.rootAppSidebar.organize.folder-rm-favorite']()
        : t['com.affine.rootAppSidebar.organize.folder-add-favorite']()}
    </MenuItem>
  );
};

export const TemplateFolderOperation = ({ node }: { node: FolderNode }) => {
  const t = useI18n();
  const templateDocService = useService(TemplateDocService);
  const folderTemplateService = useService(FolderTemplateService);

  const id = node.id;
  const isTemplate = useLiveData(
    useMemo(
      () => (id ? templateDocService.folderList.isTemplate$(id) : undefined),
      [id, templateDocService]
    )
  );
  const parentId = useLiveData(node.info$.map(info => info?.parentId ?? null));
  const name = useLiveData(node.name$);

  const toggleTemplate = useCallback(() => {
    if (!id) return;
    templateDocService.folderList.setTemplate(id, !isTemplate);
  }, [id, isTemplate, templateDocService]);

  const createFromTemplate = useAsyncCallback(async () => {
    if (!id) return;
    await folderTemplateService.instantiate(id, parentId);
    notify.success({
      title: t['com.affine.folder-template.created-notify.title'](),
    });
  }, [folderTemplateService, id, parentId, t]);

  const createWorkspaceFromFolder = useAsyncCallback(async () => {
    if (!id) return;
    await folderTemplateService.instantiateAsWorkspace(id, name || 'Untitled');
    notify.success({
      title: t['com.affine.folder-template.workspace-created-notify.title'](),
    });
  }, [folderTemplateService, id, name, t]);

  if (!id) {
    return null;
  }

  return (
    <>
      <MenuItem prefixIcon={<TemplateIcon />} onClick={toggleTemplate}>
        {isTemplate
          ? t['com.affine.folder-template.remove-as-template']()
          : t['com.affine.folder-template.save-as-template']()}
      </MenuItem>
      {isTemplate ? (
        <MenuItem prefixIcon={<TemplateIcon />} onClick={createFromTemplate}>
          {t['com.affine.folder-template.new-from-template']()}
        </MenuItem>
      ) : null}
      <MenuItem
        prefixIcon={<LocalWorkspaceIcon />}
        onClick={createWorkspaceFromFolder}
      >
        {t['com.affine.folder-template.new-workspace-from-folder']()}
      </MenuItem>
    </>
  );
};
